import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { CinematicBurst } from './CinematicBurst';
import { RevealPhotos } from './RevealPhotos';
import { useRevealViewport, PreparingIndicator } from '../useRevealViewport';
import type { FullCanvasSceneProps } from './types';
import { STYLE_ALIGNMENT } from './types';
import { AdaptiveMessageBody } from '../cinematic/adaptive/AdaptiveMessageBody';
import { getReadingRevealCompleteMs } from './readingDwell';

import { DepthRevealLayer } from '../cinematic/DepthRevealLayer';
const EASE = [0.22, 0.61, 0.36, 1] as const;

const GARDEN_COLORS = [
  '#f43f5e', '#ec4899', '#e11d48', '#10b981', '#059669',
  '#84cc16', '#22c55e', '#fbbf24', '#f97316', '#a855f7',
  '#c026d3', '#14b8a6', '#d946ef', '#fb7185', '#34d399',
];

export function FullCanvasBlooming({ recipientName, relationship, message, photoUrls, publishedDate, onComplete, config, startAtMessage = false }: FullCanvasSceneProps) {
  const [phase, setPhase] = useState<'earth' | 'burst' | 'grow' | 'text' | 'done'>(startAtMessage ? 'text' : 'earth');
  const [burstFired, setBurstFired] = useState(false);
  const lines = message.split('\n').filter(l => l.trim());
  const lineDelay = 0.6;
  const { isMobile, burstOriginY, timingMultiplier: tm, particleScale } = useRevealViewport();

  const t = (ms: number) => ms * tm;

  useEffect(() => {
    if (startAtMessage) {
      const tDone = setTimeout(() => { setPhase('done'); onComplete(); }, getReadingRevealCompleteMs({
        message,
        mode: config?.style.readingRhythm ?? 'sentence',
        pacing: config?.pacing ?? 1,
        startDelay: 1.2,
      }));
      return () => clearTimeout(tDone);
    }
    const t1 = setTimeout(() => { setPhase('burst'); setBurstFired(true); }, t(4000));
    const t2 = setTimeout(() => setPhase('grow'), t(7000));
    const t3 = setTimeout(() => setPhase('text'), t(11500));
    const totalDuration = t(11500) / 1000 + 2.0 + lines.length * lineDelay + 4.0;
    const t4 = setTimeout(() => { setPhase('done'); onComplete(); }, totalDuration * 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [config?.pacing, config?.style.readingRhythm, lines.length, message, onComplete, startAtMessage, tm]);

  const isPostBurst = phase !== 'earth';
  const isGrowOrLater = phase === 'grow' || phase === 'text' || phase === 'done';
  const isTextVisible = phase === 'text' || phase === 'done';

  const flowerCount = Math.round(16 * particleScale);
  const flowers = useMemo(() =>
    Array.from({ length: flowerCount }, (_, i) => ({
      x: 3 + (i * 6.5) % 94, y: 8 + (i * 12) % 84,
      size: 22 + Math.random() * 30, delay: 0.2 + i * 0.2,
      color: GARDEN_COLORS[i % GARDEN_COLORS.length],
      rotation: Math.random() * 60 - 30, petalCount: 5 + Math.floor(Math.random() * 3),
    })),
  [flowerCount]);

  return (
    <div className={`min-h-[100dvh] sm:min-h-screen relative ${isTextVisible ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`}>
      <motion.div className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0a1008 0%, #1a2810 30%, #0a1008 100%)' }}
        animate={{ opacity: isGrowOrLater ? 0 : 1 }}
        transition={{ duration: 3.0, ease: EASE as any }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/90 via-green-50/70 to-teal-50/60" />
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% -10%, rgba(251,191,36,0.06) 0%, transparent 50%)' }} />

      {/* Mobile preparing indicator */}
      {isMobile && <PreparingIndicator visible={phase === 'earth'} color="rgba(16,185,129,0.7)" />}

      {phase === 'earth' && (
        <>
          {[0, 1, 2, 3, 4, 5, 6].map(i => (
            <motion.div key={`seed-${i}`} className="absolute rounded-full pointer-events-none"
              style={{
                width: isMobile ? 5 + i % 3 : 3 + i % 3, height: isMobile ? 5 + i % 3 : 3 + i % 3,
                left: `${25 + i * 8}%`, top: `${(burstOriginY - 5) + (i % 3) * 8}%`,
                background: GARDEN_COLORS[i],
                boxShadow: `0 0 12px 5px ${GARDEN_COLORS[i]}44`,
              }}
              animate={{ opacity: [0, 0.7, 0], scale: [0.5, 1.3, 0.5] }}
              transition={{ duration: 1.5, delay: i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(circle at 50% ${burstOriginY + 10}%, rgba(16,185,129,0.08) 0%, transparent 35%)` }}
            animate={{ opacity: [0, 0.8, 0.3, 0.8] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      )}

      <CinematicBurst active={burstFired} count={Math.round(65 * particleScale)} colors={GARDEN_COLORS} shape="petal" originX={50} originY={burstOriginY} spread={isMobile ? 40 : 55} ambientCount={Math.round(20 * particleScale)} glowIntensity={2.5} />

      {isGrowOrLater && (
        <>
          <motion.div className={`absolute left-0 top-0 bottom-0 ${isMobile ? 'w-8' : 'w-14'} md:w-20 pointer-events-none z-10`}
            initial={{ scaleY: 0, originY: '100%' }} animate={{ scaleY: 1 }}
            transition={{ duration: 3.0, ease: EASE as any }}
          >
            <svg viewBox="0 0 40 600" className="h-full w-full text-emerald-500/30" preserveAspectRatio="none">
              <path d="M20 600 Q8 450 20 300 Q32 150 20 0" stroke="currentColor" strokeWidth="2.5" fill="none" />
              <circle cx="15" cy="100" r="6" fill="currentColor" opacity="0.5" />
              <circle cx="25" cy="250" r="5" fill="currentColor" opacity="0.4" />
              <circle cx="12" cy="400" r="7" fill="currentColor" opacity="0.3" />
              <circle cx="28" cy="520" r="4" fill="currentColor" opacity="0.4" />
            </svg>
          </motion.div>
          <motion.div className={`absolute right-0 top-0 bottom-0 ${isMobile ? 'w-8' : 'w-14'} md:w-20 pointer-events-none z-10`}
            initial={{ scaleY: 0, originY: '0%' }} animate={{ scaleY: 1 }}
            transition={{ duration: 3.0, delay: 0.4, ease: EASE as any }}
            style={{ transform: 'scaleX(-1)' }}
          >
            <svg viewBox="0 0 40 600" className="h-full w-full text-emerald-500/30" preserveAspectRatio="none">
              <path d="M20 0 Q32 150 20 300 Q8 450 20 600" stroke="currentColor" strokeWidth="2.5" fill="none" />
              <circle cx="25" cy="180" r="5" fill="currentColor" opacity="0.4" />
              <circle cx="15" cy="380" r="6" fill="currentColor" opacity="0.5" />
              <circle cx="22" cy="500" r="4" fill="currentColor" opacity="0.3" />
            </svg>
          </motion.div>
        </>
      )}

      {isGrowOrLater && flowers.map((f, i) => (
        <motion.div key={`flower-${i}`} className="absolute pointer-events-none z-10"
          style={{ left: `${f.x}%`, top: `${f.y}%` }}
          initial={{ opacity: 0, scale: 0, rotate: f.rotation }}
          animate={{ opacity: [0, 0.7, 0.5], scale: [0, 1.3, 1], rotate: f.rotation }}
          transition={{ delay: f.delay, duration: 1.2, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <svg width={f.size} height={f.size} viewBox="0 0 40 40">
            {Array.from({ length: f.petalCount }, (_, p) => (
              <ellipse key={p} cx="20" cy="8" rx="6" ry="10" fill={f.color} opacity="0.6"
                transform={`rotate(${(360 / f.petalCount) * p} 20 20)`}
              />
            ))}
            <circle cx="20" cy="20" r="5" fill="#fbbf24" />
            <circle cx="20" cy="20" r="3" fill="#fcd34d" />
          </svg>
        </motion.div>
      ))}

      <motion.div
        className="relative z-30 py-12 sm:py-16 md:py-20 px-4 sm:px-8 md:px-12"
        initial={{ opacity: 0 }} animate={{ opacity: isTextVisible ? 1 : 0 }}
        transition={{ duration: startAtMessage ? 1.8 : 1.2 }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isTextVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1.0, ease: EASE as any }}>
            <p className="text-xs uppercase tracking-[0.25em] text-emerald-600/60 mb-3">A blooming message</p>
            <h1 className="font-serif text-3xl md:text-4xl text-emerald-900/90 mb-8 tracking-wide">For {recipientName}</h1>
          </motion.div>

          <RevealPhotos photoUrls={photoUrls} visible={isTextVisible} delay={0.8} borderColor="rgba(16,185,129,0.4)" shadowColor="rgba(5,150,105,0.15)" />

          <div className="mb-12">
            <AdaptiveMessageBody
              message={message}
              mode={config?.style.readingRhythm ?? 'sentence'}
              pacing={config?.pacing ?? 1}
              visible={isTextVisible}
              startDelay={1.2}
              alignment={config ? STYLE_ALIGNMENT[config.styleId] : 'centered'}
              textColorClass="text-emerald-900/85"
              emphasisGlow="rgba(110,231,183,0.5)"
              typography={config?.typography ?? "serif-classic"}
            />
          </div>

          <motion.div initial={{ opacity: 0 }} animate={isTextVisible ? { opacity: 1 } : {}}
            transition={{ delay: 1.8 + lines.length * lineDelay, duration: 1.0 }}
          >
            <p className="text-lg font-serif italic text-emerald-700/70">With gratitude,</p>
            <p className="text-xl font-serif text-emerald-800/80 mt-1">— From your {getSenderPerspectiveLabel(relationship)}</p>
            <div className="mt-10 pt-6 border-t border-emerald-200/30">
              <p className="text-xs text-emerald-500/40 tracking-wider">Say It NOW • While They're Still Here</p>
              {publishedDate && <p className="text-xs text-emerald-400/30 mt-1">Shared on {publishedDate}</p>}
            </div>
          </motion.div>
        </div>
      </motion.div>
      <DepthRevealLayer active={isTextVisible} tint="rgba(16,185,129,0.55)" glow="rgba(110,231,183,0.5)" />
    </div>
  );
}
